<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { BaseIconButton } from '@cplug/design-system/components'
import { BaseButtonSize, BaseIconName } from '@cplug/design-system/components/props'
import { useI18n } from 'vue-i18n'
import { AssistantModal, useAssistantUiStore } from '@cplug/assistant-chat'
import { useAssistantEmbedLayout } from '../composables/useAssistantEmbedLayout'
import { useAssistantSidebar } from '../composables/useAssistantSidebar'

const { t } = useI18n()
const { closeAssistantSidebar } = useAssistantSidebar()
const assistantUiStore = useAssistantUiStore()
const chatContainerRef = ref<HTMLElement | null>(null)
const headerRef = ref<HTMLElement | null>(null)

useAssistantEmbedLayout(chatContainerRef)

const ERP_TOPBAR_SELECTOR = '.layout-main .layout-persistent'

function syncAssistantHeaderWithTopbar (): void {
  const toolbar = document.querySelector<HTMLElement>(ERP_TOPBAR_SELECTOR)
  const header = headerRef.value
  if (!toolbar || !header) {
    return
  }

  const { height } = toolbar.getBoundingClientRect()
  header.style.setProperty('--layout-topbar-height', `${height}px`)

  const toolbarStyles = getComputedStyle(toolbar)
  header.style.borderBottomWidth = toolbarStyles.borderBottomWidth
  header.style.borderBottomStyle = toolbarStyles.borderBottomStyle
  header.style.borderBottomColor = toolbarStyles.borderBottomColor
}

let topbarResizeObserver: ResizeObserver | null = null

onMounted(() => {
  syncAssistantHeaderWithTopbar()

  const toolbar = document.querySelector<HTMLElement>(ERP_TOPBAR_SELECTOR)
  if (!toolbar) {
    return
  }

  topbarResizeObserver = new ResizeObserver(() => {
    syncAssistantHeaderWithTopbar()
  })
  topbarResizeObserver.observe(toolbar)
})

onUnmounted(() => {
  topbarResizeObserver?.disconnect()
  topbarResizeObserver = null
})
</script>

<template>
  <div class="assistant-sidebar-panel flex h-full min-h-0 w-full flex-col bg-sidebar text-sidebar-foreground">
    <header
      ref="headerRef"
      class="assistant-sidebar-panel__header layout-topbar-height flex shrink-0 items-center gap-2 border-b border-solid border-sidebar-border bg-sidebar px-3"
    >
      <BaseIconButton
        :contained="false"
        :icon="BaseIconName.ArrowLeft"
        :size="BaseButtonSize.Small"
        :aria-label="t('Conversations')"
        data-testid="btn-assistant-sidebar-threads"
        @click="assistantUiStore.toggleThreadList()"
      />
      <span class="title-small-semibold flex-1 text-sidebar-foreground">
        {{ t('Title') }}
      </span>
      <BaseIconButton
        :contained="false"
        :icon="BaseIconName.ChevronDown"
        :size="BaseButtonSize.Small"
        :aria-label="t('Collapse')"
        data-testid="btn-assistant-sidebar-collapse"
        @click="closeAssistantSidebar"
      />
    </header>

    <div
      ref="chatContainerRef"
      class="assistant-sidebar-panel__chat relative min-h-0 flex-1 w-full overflow-hidden bg-sidebar"
    >
      <AssistantModal hide-trigger />
    </div>
  </div>
</template>

<style lang="scss">
.assistant-sidebar-panel__header {
  border-bottom-color: var(--sidebar-border);
}

/* Header duplicado do pacote (sidebar ERP já tem título + fechar) */
.assistant-sidebar-panel__chat .cplug-assistant .flex.shrink-0.items-center.border-b {
  display: none !important;
}

.dark .assistant-sidebar-panel__chat .cplug-assistant > div {
  background-color: var(--sidebar) !important;
  color: var(--sidebar-foreground) !important;
}
</style>

<i18n lang="yaml">
pt-br:
  Title: Cplug Assistente
  Conversations: Conversas
  Collapse: Recolher
</i18n>
